import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Grid, styled, Typography } from "@mui/material";
import * as Yup from "yup";
import { MRT_ColumnDef } from "material-react-table";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import CustomTable from "../../../../shared/components/table/custom-table";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import { useApi } from "../../../../shared/hooks/useApi";
import { Pagination } from "../../../../shared/hooks/useRecordStore";
import { extractTEIdFromEmail } from "../../../../shared/utils/user-utils";
import { useRecentTEIds } from "../../../../shared/hooks/useRecentTEIds";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import {
  IOpeningBalanceRecord,
  IBalanceSummaryRecord,
  IPagination,
  IFormUIState,
  IFormDisabledState,
  ReportRecord,
} from "./models/ml-report-model";
import {
  buyerSellerDetailsService,
  mlbuyerSellerDetailsService,
} from "../../../transaction-certificates/tc-service/buyer-seller-details.service";
import useNotification from "../../../../shared/hooks/useNotification";
import { ActionsMenu } from "../../../../shared/components/table/custom-actions";

const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

const ButtonContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  marginBottom: "24px",
  alignItems: "center",
});

function transformMLLatestBalRecord(
  item: any
): IBalanceSummaryRecord & {
  standard?: string;
  isNegative?: boolean;
  certifiedOrganizationName?: string;
} {
  const balance = item.Balance != null
    ? (typeof item.Balance === "number" ? item.Balance : parseFloat(item.Balance) || 0)
    : 0;
  const isNegativeFlag =
    item["Is Negative"] != null &&
    item["Is Negative"] !== "" &&
    /^(y|yes|true|1)$/i.test(String(item["Is Negative"]).trim());
  const certifiedOrgName =
    item["Certified Organization Name"] ??
    item["Account Name"] ??
    item["CO Name"] ??
    "";

  return {
    coTeId: item["TE-ID"] ?? "",
    coName: certifiedOrgName,
    // Additional key for dashboard usage
    certifiedOrganizationName: certifiedOrgName,
    scNo: item["SC Number"] ?? "",
    country: item.Country ?? "",
    rawMaterial: item.RM ?? "",
    productCategory: item.PC ?? "",
    productDetail: item.PD ?? "",
    certifiedWeight: balance,
    lastUpdatedDate: item["Last Updated Date"] ?? "",
    standard: item.Standard ?? "",
    balanceType: isNegativeFlag || balance < 0 ? "negative" : balance > 0 ? "positive" : "zero",
    isNegative: isNegativeFlag || balance < 0,
  };
}

const MaterialLedgerReport = () => {
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const { resetAllDatas } = useFormActions("");
  const { notify } = useNotification();
  const { cbLicensingCode } = useTransactionCertificateStore();
  const { recentTEIds, saveRecentlySelectedTEId } = useRecentTEIds();
  const { executeGet } = useApi();

  const handleViewBalanceSummary = (row: any) => {
    const teId = row.original.coTeId ?? "";
    const rmCode = row.original.rawMaterial ?? "";
    const pc = row.original.productCategory ?? "";
    const pd = row.original.productDetail ?? "";
    const params = new URLSearchParams();
    if (pc) params.set("pc", pc);
    if (pd) params.set("pd", pd);
    const query = params.toString();
    navigate(
      `/ml/material-ledger/${encodeURIComponent(teId)}/${encodeURIComponent(rmCode)}${query ? `?${query}` : ""}`
    );
  };

  const [reportType, setReportType] = useState<"opening-balance" | "balance-summary">("opening-balance");
  const [productCategory, setProductCategory] = useState<any[]>([]);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [rawMaterials, setRawMaterials] = useState<any[]>([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [isCOUser, setIsCOUser] = useState(false);
  const [isResetEnabled, setResetEnable] = useState(false);
  const [disable, setDisable] = useState<IFormDisabledState>({
    coName: false,
    coTeId: false,
    rmCode: true,
  });
  const [visible, setVisible] = useState<IFormUIState>({
    coName: true,
    coTeId: true,
    coNameLabel: true,
    coTeIdLabel: true,
  });
  const [filteredRMCodes, setFilteredRMCodes] = useState<any[]>([]);
  const [tableData, setTableData] = useState<ReportRecord[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);
  /** Per-tab filter values so each tab keeps its own filters when switching */
  const [openingBalanceFormValues, setOpeningBalanceFormValues] = useState<Record<string, any>>({});
  const [balanceSummaryFormValues, setBalanceSummaryFormValues] = useState<Record<string, any>>({});
  const [hasFiltersInForm, setHasFiltersInForm] = useState(false);
  const [hasFormChangedFromLastSearch, setHasFormChangedFromLastSearch] = useState(false);
  const [hasSearchedOpening, setHasSearchedOpening] = useState(false);
  const [hasSearchedSummary, setHasSearchedSummary] = useState(false);
  const hasAutoLoadedOpeningBalanceRef = useRef(false);
  const lastSearchValuesRef = useRef<{ opening: Record<string, any>; summary: Record<string, any> }>({ opening: {}, summary: {} });
  const hasSearchedOpeningRef = useRef(false);
  const hasSearchedSummaryRef = useRef(false);

  const normalizeArrayField = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item) => (item && typeof item === "object" && "value" in item ? String(item.value) : String(item))).sort();
  };

  const normalizeSingleValue = (v: any): string => {
    if (v == null || v === "") return "";
    if (typeof v === "object" && v !== null && "value" in v) return String(v.value ?? "").trim();
    return String(v).trim();
  };

  const getFilterRelevantValues = useCallback((values: Record<string, any>, type: "opening-balance" | "balance-summary") => {
    if (!values) return {};
    const base: Record<string, any> = {
      coTeID: normalizeSingleValue(values.coTeID),
      rmCode: normalizeSingleValue(values.rmCode),
      buyersCountry: normalizeArrayField(values.buyersCountry ?? []),
      productCategory: normalizeArrayField(values.productCategory ?? []),
      productDetail: normalizeArrayField(values.productDetail ?? []),
    };
    if (type === "opening-balance") {
      base.openingBalanceStatus = normalizeSingleValue(values.openingBalanceStatus);
    } else {
      base.balanceType = normalizeSingleValue(values.balanceType);
      base.lastUpdatedStartDate = normalizeSingleValue(values.lastUpdatedStartDate);
      base.lastUpdatedEndDate = normalizeSingleValue(values.lastUpdatedEndDate);
    }
    return base;
  }, []);

  const areFormValuesEqual = useCallback(
    (current: Record<string, any>, stored: Record<string, any>, type: "opening-balance" | "balance-summary") => {
      const a = getFilterRelevantValues(current, type);
      const b = getFilterRelevantValues(stored, type);
      return JSON.stringify(a) === JSON.stringify(b);
    },
    [getFilterRelevantValues]
  );

  const hasFiltersApplied = useCallback((values: Record<string, any>, type: "opening-balance" | "balance-summary") => {
    if (!values) return false;
    return !!(
      values.coTeID ||
      values.rmCode ||
      (values.buyersCountry?.length > 0) ||
      (values.productCategory?.length > 0) ||
      (values.productDetail?.length > 0) ||
      (type === "opening-balance" && values.openingBalanceStatus === "Completed") ||
      (type === "balance-summary" && (values.balanceType === "positive" || values.balanceType === "zero" || values.lastUpdatedStartDate || values.lastUpdatedEndDate))
    );
  }, []);

  const getCountry = async () => {
    try {
      const countryCodesData = require("../../../../shared/data/country-codes.json");
      const priorityCountries = ['CN', 'US', 'IN', 'BD', 'TR', 'PK', 'VN', 'GB', 'DE', 'IT', 'ES', 'FR', 'JP', 'KR', 'TW', 'HK', 'TH', 'ID', 'MY', 'SG'];
      const allCountries = countryCodesData.map((country: any) => ({
        value: country.code,
        label: `${country.name} (${country.code})`,
        isPriority: priorityCountries.includes(country.code)
      }));
      const transformedData = allCountries.sort((a: any, b: any) => {
        if (a.isPriority && !b.isPriority) return -1;
        if (!a.isPriority && b.isPriority) return 1;
        return a.label.localeCompare(b.label);
      });
      setCountries(transformedData);
    } catch (err) {
      console.error(`Error loading static country codes: ${err}`);
    }
  };

  const fetchProductCategory = async () => {
    try {
      const result: any = await dtrackItService.getProductCategory();
      const prdtCategory = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setProductCategory(prdtCategory);
    } catch (err) {
      console.error("Error fetching product category", err);
    }
  };

  const fetchProductDetails = async () => {
    try {
      const result: any = await dtrackItService.getProductDetailsCode();
      const prdtDetails = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setProductDetails(prdtDetails);
    } catch (err) {
      console.error("Error fetching product details", err);
    }
  };

  const fetchRawMaterialOptions = async () => {
    try {
      const result: any = await dtrackItService.getRawMaterialCodes();
      const options = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setRawMaterials(options);
    } catch (err) {
      console.error("Error fetching raw material codes", err);
    }
  };

  const getRawMatStandardCodes = async () => {
    const response = await executeGet(
      TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
    );
    if (response?.data) {
      setCertifiedRawMaterialCodes(response.data);
    }
  };

  useEffect(() => {
    resetAllDatas();
    Promise.all([
      fetchProductCategory(),
      fetchProductDetails(),
      fetchRawMaterialOptions(),
      getRawMatStandardCodes(),
      getCountry(),
    ]);
  }, []);
  

  // CO User Detection
  useEffect(() => {
    const checkCOUser = async () => {
      const userEmail = cbLicensingCode;
      if (userEmail) {
        const extractedTEId = extractTEIdFromEmail(userEmail);
        if (extractedTEId) {
          setIsCOUser(true);
          if (formRef.current) {
            formRef.current.setFieldValue("coTeID", extractedTEId);
          }
          setVisible((prev) => ({
            ...prev,
            coName: false,
            coTeId: false,
            coNameLabel: false,
            coTeIdLabel: false,
          }));
          setDisable((prev) => ({
            ...prev,
            coName: true,
            coTeId: true,
          }));
        }
      }
    };
    checkCOUser();
  }, [cbLicensingCode]);

  // Auto-load Opening Balance report when landing on the page (default tab)
  useEffect(() => {
    if (
      reportType === "opening-balance" &&
      cbLicensingCode &&
      !hasAutoLoadedOpeningBalanceRef.current
    ) {
      hasAutoLoadedOpeningBalanceRef.current = true;
      loadOpeningBalanceData();
    }
  }, [reportType, cbLicensingCode]);

  // Load initial data when Opening Balance button is clicked
  const loadOpeningBalanceData = async () => {
    setIsLoading(true);
    setResetEnable(true);
    setPagination({ pageIndex: 0, pageSize: 10 });
    try {
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };

      const response = await executeGet(TCAppConfig.ML_OB_REPORT, queryParams);
      
      if (response?.data) {
        const recordsData = response.data.response || [];
        const transformedData = recordsData.map((item: any) => {
          const certifiedOrgName =
            item["Certified Organization Name"] ||
            item["Account Name"] ||
            item["CO Name"] ||
            "";
          return {
            coTeId: item["TE-ID"] || "",
            coName: certifiedOrgName,
            // Additional key for dashboard usage
            certifiedOrganizationName: certifiedOrgName,
            Country: item.Country || "",
            rawMaterial: item.RM || "",
            productCategory: item.PC || "",
            productDetail: item.PD || "",
            transactionDate: item["Opening Balance Transaction Date"] ?? "pending",
            certifiedWeight:
              item["Opening Balance"] !== null && item["Opening Balance"] !== undefined
                ? item["Opening Balance"]
                : "pending",
            scNo: item["SC Number"] || "",
            standard: item.Standard || "",
            obSubmitted: item["OB Submitted"] || "N",
          };
        });
        
        setTableData(transformedData);
        setRowCount(response.data.no_of_records || transformedData.length || 0);
        setHasFormChangedFromLastSearch(false);
        if (transformedData.length === 0) {
          notify("No data found", "warning");
        }
      } else {
        notify("No data found", "warning");
        setTableData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error loading opening balance data:", error);
      notify("Error fetching report data", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial data when Balance Summary button is clicked
  const loadBalanceSummaryData = async () => {
    setIsLoading(true);
    setResetEnable(true);
    setPagination({ pageIndex: 0, pageSize: 10 });
    try {
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };

      const response = await executeGet(TCAppConfig.ML_LATEST_BAL, queryParams);
      
      if (response?.data) {
        const recordsData = response.data.response || [];
        const transformedData = recordsData.map((item: any) =>
          transformMLLatestBalRecord(item)
        );

        setTableData(transformedData);
        setRowCount(response.data.no_of_records ?? transformedData.length ?? 0);
        setHasFormChangedFromLastSearch(false);
        if (transformedData.length === 0) {
          notify("No data found", "warning");
        }
      } else {
        notify("No data found", "warning");
        setTableData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error loading balance summary data:", error);
      notify("Error fetching report data", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Normalize standards string to a list of comparable codes (handles "(RMS)" and "RMS, RWS" formats)
  const extractApprovedStandards = useCallback((rawStandards: any): string[] => {
    if (!rawStandards) return [];
  
    const standardsString = Array.isArray(rawStandards)
      ? rawStandards.join(",")
      : String(rawStandards);
  
    const parenthesizedCodes =
      [...standardsString.matchAll(/\(([^)]+)\)/g)].map((m) =>
        m[1].trim().toUpperCase()
      );
  
    const fallbackCodes = standardsString
      .split(/[,/]/)
      .map((t) => t.trim().toUpperCase())
      .filter((t) => /^[A-Z0-9-]+$/.test(t));
  
    return [...new Set([...parenthesizedCodes, ...fallbackCodes])];
  }, []);
  

  const updateFilteredRMCodes = (value: any) => {
    const codes = extractApprovedStandards(value?.standards);
    const approvedSet = new Set(codes);
    const filtered = certifiedRawMaterialCodes.filter((item) =>
      item?.standard
        .split("/")
        .some((part: any) =>
          approvedSet.has(String(part).trim().toUpperCase())
        )
    );
    const validMaterialCodes = filtered.map((item) => item.material_code);
    setFilteredRMCodes(
      rawMaterials.filter((item) => validMaterialCodes.includes(item.value))
    );
  };

  const handleCoNameValueChange = (value: any) => {
    if (value) {
      // Fetch full organization details to get standards information
      const fetchFullDetails = async () => {
        try {
          const queryParams: Record<string, any> = {
            query_param: value.TE_ID,
            offset: 0,
            name: "",
            tcCbLicensingCode: cbLicensingCode,
          };
          const response = await mlbuyerSellerDetailsService.getMLCODetails(queryParams);
          if (response && response.data && response.data.length > 0) {
            updateFilteredRMCodes(response.data[0]);
          } else {
            updateFilteredRMCodes(value);
          }
        } catch (error) {
          console.error("Error fetching full organization details:", error);
          // Fallback to using search result
          updateFilteredRMCodes(value);
        }
      };
      
      fetchFullDetails();
      
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("rmCode", "");
      setVisible((prev) => ({
        ...prev,
        coTeId: false,
        coNameLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: true,
        rmCode: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("rmCode", "");
      setVisible((prev) => ({
        ...prev,
        coTeId: true,
        coNameLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coTeId: false,
        rmCode: true,
      }));
    }
  };

  const handleCoTeIdValueChange = (value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      // Multi-select: store comma-separated TE-IDs for filtering
      const selectedList = value.filter((v: any) => v && v.TE_ID);
      const teIds = selectedList
        .map((v: any) => v.TE_ID)
        .filter((id: any) => !!id)
        .join(",");

      // Persist all selected TE-IDs into recent list so they appear
      // next time from useRecentTEIds (local storage backed hook).
      selectedList.forEach((v: any) => {
        try {
          saveRecentlySelectedTEId(v);
        } catch (e) {
          console.error("Error saving recent TE-ID (multi-select):", e);
        }
      });

      formRef.current.setFieldValue("coTeID", teIds);

      // Populate organization name as comma-separated list and disable editing
      const orgNames = selectedList
        .map((v: any) => v.account_name)
        .filter((name: any) => !!name);
      formRef.current.setFieldValue("coName", orgNames.join(", "));

      setVisible((prev) => ({
        ...prev,
        coName: false, // show text field (not search)
        coTeIdLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: true, // disable org name editing
        rmCode: false, // enable raw material selection when TE-IDs exist
      }));

      // Aggregate standards from the selected search results themselves
      const standardsList: string[] = [];
      selectedList.forEach((d: any) => {
        if (d && d.standards) {
          standardsList.push(
            Array.isArray(d.standards) ? d.standards.join(",") : String(d.standards)
          );
        }
      });

      const combinedStandards = standardsList.join(",");
      if (combinedStandards) {
        updateFilteredRMCodes({ standards: combinedStandards });
      } else {
        setFilteredRMCodes([]);
      }
    } else if (value) {
      saveRecentlySelectedTEId(value);

      // For single-select, if standards already exist on the selected option,
      // avoid extra API call and use them directly; otherwise fall back to API.
      const hasInlineStandards = !!value.standards;
      if (hasInlineStandards) {
        updateFilteredRMCodes(value);
      } else {
        const fetchFullDetails = async () => {
          try {
            const queryParams: Record<string, any> = {
              query_param: value.TE_ID,
              offset: 0,
              name: "",
              tcCbLicensingCode: cbLicensingCode,
            };
            const response = await mlbuyerSellerDetailsService.getMLCODetails(
              queryParams
            );
            if (response && response.data && response.data.length > 0) {
              updateFilteredRMCodes(response.data[0]);
            } else {
              updateFilteredRMCodes(value);
            }
          } catch (error) {
            console.error("Error fetching full organization details:", error);
            // Fallback to using search result
            updateFilteredRMCodes(value);
          }
        };

        fetchFullDetails();
      }

      formRef.current.setFieldValue("coTeID", value.TE_ID);
      formRef.current.setFieldValue("coName", value.account_name);
      formRef.current.setFieldValue("rmCode", "");
      setVisible((prev) => ({
        ...prev,
        coName: false,
        coTeIdLabel: false,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: true,
        rmCode: !value?.TE_ID,
      }));
    } else {
      setFilteredRMCodes([]);
      formRef.current.setFieldValue("coTeID", "");
      formRef.current.setFieldValue("coName", "");
      formRef.current.setFieldValue("rmCode", "");
      setVisible((prev) => ({
        ...prev,
        coName: true,
        coTeIdLabel: true,
      }));
      setDisable((prev) => ({
        ...prev,
        coName: false,
        rmCode: true,
      }));
    }
  };

  const fetchTENameDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: "",
        offset: offset,
        name: query,
        tcCbLicensingCode: cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by name:", error);
      return [];
    }
  };

  const fetchTEIdDetails = async (
    query: string,
    offset: number,
    type: string
  ) => {
    try {
      const queryParams: Record<string, any> = {
        query_param: query,
        offset: offset,
        name: "",
        tcCbLicensingCode: cbLicensingCode,
      };
      const response = await mlbuyerSellerDetailsService.getMLCODetails(
        queryParams
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching TE details by ID:", error);
      return [];
    }
  };

  const getFormConfig = () => {
    const defaultInitialValues = {
      coName: "",
      coTeID: "",
      // Allow both single and multi-select RM codes; Formik value will be an array
      // when multi-select is enabled.
      rmCode: [] as string[],
      buyersCountry: [],
      productCategory: [],
      productDetail: [],
      openingBalanceStatus: "",
      balanceType: "negative",
      lastUpdatedStartDate: "",
      lastUpdatedEndDate: "",
    };
    const baseConfig = {
      initialValues: {
        ...defaultInitialValues,
        ...(reportType === "opening-balance" ? openingBalanceFormValues : balanceSummaryFormValues),
      },
      validationSchema: Yup.object().shape({
        coName: Yup.string(),
        coTeID: Yup.string(),
        // rmCode can be a single string or an array of strings (for multi-select)
        rmCode: Yup.mixed<string | string[]>().nullable(),
        buyersCountry: Yup.array(),
        productCategory: Yup.array(),
        productDetail: Yup.array(),
        openingBalanceStatus: Yup.string(),
        balanceType: Yup.string(),
        lastUpdatedStartDate: Yup.string(),
        lastUpdatedEndDate: Yup.string().test(
          "end-on-or-after-start",
          "Last Updated End Date must be on or after Last Updated Start Date",
          function (value) {
            const { lastUpdatedStartDate } = this.parent;
            if (!value || !lastUpdatedStartDate) return true;
            return new Date(value) >= new Date(lastUpdatedStartDate);
          }
        ),
      }),
      onValuesChange: (values: Record<string, any>) => {
        setHasFiltersInForm(hasFiltersApplied(values, reportType));

        const stored =
          reportType === "opening-balance"
            ? (hasSearchedOpeningRef.current
                ? lastSearchValuesRef.current.opening
                : {
                    coTeID: "",
                    rmCode: "",
                    buyersCountry: [],
                    productCategory: [],
                    productDetail: [],
                    openingBalanceStatus: "",
                  })
            : (hasSearchedSummaryRef.current
                ? lastSearchValuesRef.current.summary
                : {
                    coTeID: "",
                    rmCode: "",
                    buyersCountry: [],
                    productCategory: [],
                    productDetail: [],
                    balanceType: "negative",
                    lastUpdatedStartDate: "",
                    lastUpdatedEndDate: "",
                  });

        setHasFormChangedFromLastSearch(!areFormValuesEqual(values, stored, reportType));
      },
    };

    if (reportType === "opening-balance") {
      return {
        ...baseConfig,
        sections: [
          {
            collapsible: false,
            fields: [
              {
                label: "Certified Organization Name",
                name: "coName",
                type: "search",
                gridSize: { xs: 12, md: 4 },
                required: false,
                visible: () => visible.coName && !isCOUser,
                props: {
                  placeholder: "Search Organization Name",
                  variant: "outlined",
                  fullWidth: true,
                  displayKey: "account_name",
                  onChange: handleCoNameValueChange,
                  onSearch: (query: string, offset: number) => {
                    return fetchTENameDetails(query, offset, "name");
                  },
                },
              },
              {
                label: "Certified Organization Name",
                name: "coName",
                type: "text",
                gridSize: { xs: 12, md: 4 },
                required: false,
                visible: () => !visible.coName || isCOUser,
                disabled: disable.coName || isCOUser,
                props: {
                  placeholder: "Enter Organization Name",
                  variant: "outlined",
                  fullWidth: true,
                },
              },
              {
                label: "Certified Organization TE-ID",
                name: "coTeID",
                type: "search",
                gridSize: { xs: 12, md: 4 },
                required: false,
                visible: () => visible.coTeId && !isCOUser,
                props: {
                  placeholder: "Search by TE-ID",
                  variant: "outlined",
                  fullWidth: true,
                  displayKey: "TE_ID",
                  restrictInputFormat: visible.coTeIdLabel,
                  onChange: handleCoTeIdValueChange,
                  fetchOnEmptyFocus: true,
                  // Trigger TE-ID search API only after 3 characters
                  minSearchLength: 3,
                  enableListFilterSearch: true,
                  multiple: true,
                  onSearch: (query: string, offset: number) => {
                    if (!query || query.trim() === "") {
                      return Promise.resolve(
                        recentTEIds.length > 0 ? recentTEIds : []
                      );
                    }
                    return fetchTEIdDetails(query, offset, "query");
                  },
                },
              },
              {
                label: "Certified Organization TE-ID",
                name: "coTeID",
                type: "text",
                gridSize: { xs: 12, md: 4 },
                required: false,
                visible: () => !visible.coTeId || isCOUser,
                disabled: disable.coTeId || isCOUser,
                props: {
                  placeholder: "Enter TE-ID",
                  variant: "outlined",
                  fullWidth: true,
                },
              },
              {
                label: "Select Raw Material Code",
                name: "rmCode",
                type: "select",
                gridSize: { xs: 12, md: 4 },
                required: false,
                props: (values: any) => ({
                  options: filteredRMCodes,
                  placeholder: "Select Raw Material Code",
                  disabled: disable.rmCode,
                  enableSearch: true,
                  multiple: typeof values.coTeID === "string" && values.coTeID.includes(","),
                }),
              },
              {
                label: "Buyer's Country/Area",
                name: "buyersCountry",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: countries,
                  placeholder: "Select Buyer's Country",
                  enableSearch: true,
                },
              },
              {
                label: "Product Category",
                name: "productCategory",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: productCategory,
                  placeholder: "Select Product Category",
                  enableSearch: true,
                },
              },
              {
                label: "Product Detail",
                name: "productDetail",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: productDetails,
                  placeholder: "Select Product Detail",
                  enableSearch: true,
                },
              },
              {
                label: "Opening Balance Submission Status",
                name: "openingBalanceStatus",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  options: [
                    { value: "Completed", label: "Completed" },
                    { value: "Not Completed", label: "Not Completed" },
                  ],
                  placeholder: "Select submission status",
                },
              },
            ],
          },
        ],
        handleSubmit: handleOpeningBalanceSubmit,
      };
    } else {
      return {
        ...baseConfig,
        sections: [
          {
            collapsible: false,
            fields: [
              {
                label: "Certified Organization Name",
                name: "coName",
                type: "search",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                visible: () => visible.coName && !isCOUser,
                props: {
                  placeholder: "Search Organization Name",
                  variant: "outlined",
                  fullWidth: true,
                  displayKey: "account_name",
                  onChange: handleCoNameValueChange,
                  onSearch: (query: string, offset: number) => {
                    return fetchTENameDetails(query, offset, "name");
                  },
                },
              },
              {
                label: "Certified Organization Name",
                name: "coName",
                type: "text",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                visible: () => !visible.coName || isCOUser,
                disabled: disable.coName || isCOUser,
                props: {
                  placeholder: "Enter Organization Name",
                  variant: "outlined",
                  fullWidth: true,
                },
              },
              {
                label: "Certified Organization TE-ID",
                name: "coTeID",
                type: "search",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                visible: () => visible.coTeId && !isCOUser,
                props: {
                  placeholder: "Search by TE-ID",
                  variant: "outlined",
                  fullWidth: true,
                  displayKey: "TE_ID",
                  restrictInputFormat: visible.coTeIdLabel,
                  onChange: handleCoTeIdValueChange,
                  fetchOnEmptyFocus: true,
                  // Trigger TE-ID search API only after 3 characters
                  minSearchLength: 3,
                  enableListFilterSearch: true,
                  multiple: true,
                  onSearch: (query: string, offset: number) => {
                    if (!query || query.trim() === "") {
                      return Promise.resolve(
                        recentTEIds.length > 0 ? recentTEIds : []
                      );
                    }
                    return fetchTEIdDetails(query, offset, "query");
                  },
                },
              },
              {
                label: "Certified Organization TE-ID",
                name: "coTeID",
                type: "text",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                visible: () => !visible.coTeId || isCOUser,
                disabled: disable.coTeId || isCOUser,
                props: {
                  placeholder: "Enter TE-ID",
                  variant: "outlined",
                  fullWidth: true,
                },
              },
              {
                label: "Select Raw Material Code",
                name: "rmCode",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: (values: any) => ({
                  options: filteredRMCodes,
                  placeholder: "Select Raw Material Code",
                  disabled: disable.rmCode,
                  enableSearch: true,
                  multiple: typeof values.coTeID === "string" && values.coTeID.includes(","),
                }),
              },
              {
                label: "Buyer's Country/Area",
                name: "buyersCountry",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: countries,
                  placeholder: "Select Buyer's Country",
                  enableSearch: true,
                },
              },
              {
                label: "Balance Type",
                name: "balanceType",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  options: [
                    { value: "positive", label: "Positive" },
                    { value: "negative", label: "Negative" },
                    { value: "zero", label: "Zero" },
                  ],
                  placeholder: "Select balance type",
                },
              },
              {
                label: "Product Category",
                name: "productCategory",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: productCategory,
                  placeholder: "Select Product Category",
                  enableSearch: true,
                },
              },
              {
                label: "Product Detail",
                name: "productDetail",
                type: "select",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  multiple: true,
                  options: productDetails,
                  placeholder: "Select Product Detail",
                  enableSearch: true,
                },
              },
              {
                label: "Last Updated Start Date",
                name: "lastUpdatedStartDate",
                type: "date",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  placeholder: "Last Updated Start Date",
                },
              },
              {
                label: "Last Updated End Date",
                name: "lastUpdatedEndDate",
                type: "date",
                gridSize: { xs: 12, md: 6, lg: 3 },
                required: false,
                props: {
                  placeholder: "Last Updated End Date",
                },
              },
            ],
          },
        ],
        handleSubmit: handleBalanceSummarySubmit,
      };
    }
  };

  const handleOpeningBalanceSubmit = async (values: any) => {
    lastSearchValuesRef.current.opening = { ...values };
    hasSearchedOpeningRef.current = true;
    setOpeningBalanceFormValues(values);
    setHasFormChangedFromLastSearch(false);
    setHasSearchedOpening(true);
    setIsLoading(true);
    setResetEnable(true);
    setPagination({ pageIndex: 0, pageSize: 10 });
    try {
      // Build query parameters with all filter values
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };

      // Add optional filters if they have values
      if (values.coTeID) {
        queryParams.teId = values.coTeID;
      }
      
      if (values.rmCode) {
        queryParams.rm = Array.isArray(values.rmCode)
          ? values.rmCode.join(',')
          : values.rmCode;
      }
      
      if (values.buyersCountry && values.buyersCountry.length > 0) {
        queryParams.country = values.buyersCountry.join(',');
      }
      
      if (values.productCategory && values.productCategory.length > 0) {
        queryParams.pc = values.productCategory.join(',');
      }
      
      if (values.productDetail && values.productDetail.length > 0) {
        queryParams.pd = values.productDetail.join(',');
      }
      
      if (values.openingBalanceStatus) {
        queryParams.obSubmissionStatus = values.openingBalanceStatus === "Completed" ? "Y" : "N";
      }

      const response = await executeGet(TCAppConfig.ML_OB_REPORT, queryParams);
      
      if (response?.data) {
        const recordsData = response.data.response || [];
        const transformedData = recordsData.map((item: any) => {
          const certifiedOrgName =
            item["Certified Organization Name"] ||
            item["Account Name"] ||
            item["CO Name"] ||
            "";
          return {
            coTeId: item["TE-ID"] || "",
            coName: certifiedOrgName,
            // Additional key for dashboard usage
            certifiedOrganizationName: certifiedOrgName,
            Country: item.Country || "",
            rawMaterial: item.RM || "",
            productCategory: item.PC || "",
            productDetail: item.PD || "",
            transactionDate: item["Opening Balance Transaction Date"] ?? "pending",
            certifiedWeight:
              item["Opening Balance"] !== null && item["Opening Balance"] !== undefined
                ? item["Opening Balance"]
                : "pending",
            scNo: item["SC Number"] || "",
            standard: item.Standard || "",
            obSubmitted: item["OB Submitted"] || "N",
          };
        });
        
        setTableData(transformedData);
        setRowCount(response.data.no_of_records || transformedData.length || 0);
        
        if (transformedData.length === 0) {
          notify("No data found", "warning");
        }
      } else {
        notify("No data found", "warning");
        setTableData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching opening balance data:", error);
      notify("Error fetching report data", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBalanceSummarySubmit = async (values: any) => {
    lastSearchValuesRef.current.summary = { ...values };
    hasSearchedSummaryRef.current = true;
    setBalanceSummaryFormValues(values);
    setHasFormChangedFromLastSearch(false);
    setHasSearchedSummary(true);
    setIsLoading(true);
    setResetEnable(true);
    setPagination({ pageIndex: 0, pageSize: 10 });
    try {
      // Build query parameters with all filter values
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };

      // Add optional filters if they have values
      if (values.coTeID) {
        queryParams.teId = values.coTeID;
      }
      
      if (values.rmCode) {
        queryParams.rm = Array.isArray(values.rmCode)
          ? values.rmCode.join(',')
          : values.rmCode;
      }
      
      if (values.buyersCountry && values.buyersCountry.length > 0) {
        queryParams.country = values.buyersCountry.join(',');
      }
      
      if (values.productCategory && values.productCategory.length > 0) {
        queryParams.pc = values.productCategory.join(',');
      }
      
      if (values.productDetail && values.productDetail.length > 0) {
        queryParams.pd = values.productDetail.join(',');
      }
      
      if (values.balanceType) {
        queryParams.balanceType = values.balanceType;
      }
      
      if (values.lastUpdatedStartDate) {
        queryParams.lastUpdatedStartDate = values.lastUpdatedStartDate;
      }
      
      if (values.lastUpdatedEndDate) {
        queryParams.lastUpdatedEndDate = values.lastUpdatedEndDate;
      }

      const response = await executeGet(TCAppConfig.ML_LATEST_BAL, queryParams);
      
      if (response?.data) {
        const recordsData = response.data.response || [];
        const transformedData = recordsData.map((item: any) =>
          transformMLLatestBalRecord(item)
        );

        setTableData(transformedData);
        setRowCount(response.data.no_of_records ?? transformedData.length ?? 0);

        if (transformedData.length === 0) {
          notify("No data found", "warning");
        }
      } else {
        notify("No data found", "warning");
        setTableData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching balance summary data:", error);
      notify("Error fetching report data", "error");
      setTableData([]);
      setRowCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const handleExportData = async () => {
    const hasSearched = reportType === "opening-balance"
      ? hasSearchedOpening
      : hasSearchedSummary;
    const values = hasSearched
      ? (formRef.current?.values ?? (reportType === "opening-balance" ? openingBalanceFormValues : balanceSummaryFormValues) ?? {})
      : {};

    setIsExporting(true);
    try {
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        type: reportType === "opening-balance" ? "ob" : "lb",
      };

      if (hasSearched) {
        if (reportType === "opening-balance") {
          if (values.coTeID) queryParams.teId = values.coTeID;
          if (values.rmCode) queryParams.rm = values.rmCode;
          if (values.buyersCountry?.length > 0) queryParams.country = values.buyersCountry.join(",");
          if (values.productCategory?.length > 0) queryParams.pc = values.productCategory.join(",");
          if (values.productDetail?.length > 0) queryParams.pd = values.productDetail.join(",");
          if (values.openingBalanceStatus) queryParams.obSubmissionStatus = values.openingBalanceStatus === "Completed" ? "Y" : "N";
        } else {
          if (values.coTeID) queryParams.teId = values.coTeID;
          if (values.rmCode) queryParams.rm = values.rmCode;
          if (values.buyersCountry?.length > 0) queryParams.country = values.buyersCountry.join(",");
          if (values.productCategory?.length > 0) queryParams.pc = values.productCategory.join(",");
          if (values.productDetail?.length > 0) queryParams.pd = values.productDetail.join(",");
          if (values.balanceType) queryParams.balanceType = values.balanceType;
          if (values.lastUpdatedStartDate) queryParams.lastUpdatedStartDate = values.lastUpdatedStartDate;
          if (values.lastUpdatedEndDate) queryParams.lastUpdatedEndDate = values.lastUpdatedEndDate;
        }
      }

      const response = await executeGet(TCAppConfig.ML_OB_REPORT_EXPORT, queryParams, { responseType: "blob" });
      const status = response?.status;
      const isSuccess = typeof status === "number" && status >= 200 && status < 300;
      if (isSuccess && response?.data instanceof Blob) {
        const blob = response.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = reportType === "opening-balance"
          ? `opening-balance-report-${new Date().toISOString().slice(0, 10)}.xlsx`
          : `balance-summary-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
        notify("Export successful", "success");
      } else {
        notify("Unable to export data", "error");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      notify("Unable to export data", "error");
    } finally {
      setIsExporting(false);
    }
  };

  // Auto-reload data when report type changes if data has been loaded
  useEffect(() => {
    if (isResetEnabled && tableData.length > 0 && formRef.current) {
      const values = formRef.current.values;
      if (values.coTeID) {
        if (reportType === "opening-balance") {
          handleOpeningBalanceSubmit(values);
        } else {
          handleBalanceSummarySubmit(values);
        }
      }
    }
  }, [reportType]);

  // Handle pagination changes
  useEffect(() => {
    if (isResetEnabled && formRef.current && pagination.pageIndex > 0) {
      const fetchPaginatedData = async () => {
        setIsLoading(true);
        try {
          const values = formRef.current.values;
          const queryParams: Record<string, any> = {
            cb: cbLicensingCode,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
          };

          if (reportType === "opening-balance") {
            // Add filters for opening balance
            if (values.coTeID) queryParams.teId = values.coTeID;
            if (values.rmCode)
              queryParams.rm = Array.isArray(values.rmCode)
                ? values.rmCode.join(',')
                : values.rmCode;
            if (values.buyersCountry?.length > 0) queryParams.country = values.buyersCountry.join(',');
            if (values.productCategory?.length > 0) queryParams.pc = values.productCategory.join(',');
            if (values.productDetail?.length > 0) queryParams.pd = values.productDetail.join(',');
            if (values.openingBalanceStatus) queryParams.obSubmissionStatus = values.openingBalanceStatus === "Completed" ? "Y" : "N";

            const response = await executeGet(TCAppConfig.ML_OB_REPORT, queryParams);
            if (response?.data) {
              const recordsData = response.data.response || [];
              const transformedData = recordsData.map((item: any) => {
                const certifiedOrgName =
                  item["Certified Organization Name"] ||
                  item["Account Name"] ||
                  item["CO Name"] ||
                  "";
                return {
                  coTeId: item["TE-ID"] || "",
                  coName: certifiedOrgName,
                  // Additional key for dashboard usage
                  certifiedOrganizationName: certifiedOrgName,
                  Country: item.Country || "",
                  rawMaterial: item.RM || "",
                  productCategory: item.PC || "",
                  productDetail: item.PD || "",
                  transactionDate: item["Opening Balance Transaction Date"] ?? "pending",
                  certifiedWeight:
                    item["Opening Balance"] !== null && item["Opening Balance"] !== undefined
                      ? item["Opening Balance"]
                      : "pending",
                  scNo: item["SC Number"] || "",
                  standard: item.Standard || "",
                  obSubmitted: item["OB Submitted"] || "N",
                };
              });
              setTableData(transformedData);
            }
          } else {
            // Balance summary pagination
            if (values.coTeID) queryParams.teId = values.coTeID;
            if (values.rmCode)
              queryParams.rm = Array.isArray(values.rmCode)
                ? values.rmCode.join(',')
                : values.rmCode;
            if (values.buyersCountry?.length > 0) queryParams.country = values.buyersCountry.join(',');
            if (values.productCategory?.length > 0) queryParams.pc = values.productCategory.join(',');
            if (values.productDetail?.length > 0) queryParams.pd = values.productDetail.join(',');
            if (values.balanceType) queryParams.balanceType = values.balanceType;
            if (values.lastUpdatedStartDate) queryParams.lastUpdatedStartDate = values.lastUpdatedStartDate;
            if (values.lastUpdatedEndDate) queryParams.lastUpdatedEndDate = values.lastUpdatedEndDate;

            const response = await executeGet(TCAppConfig.ML_LATEST_BAL, queryParams);
            if (response?.data) {
              const recordsData = response.data.response || [];
              const transformedData = recordsData.map((item: any) =>
                transformMLLatestBalRecord(item)
              );
              setTableData(transformedData);
            }
          }
        } catch (error) {
          console.error("Error fetching paginated data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchPaginatedData();
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  const handleReset = () => {
    if (reportType === "opening-balance") {
      setOpeningBalanceFormValues({});
    } else {
      setBalanceSummaryFormValues({});
    }
    setHasFiltersInForm(false);
    setHasFormChangedFromLastSearch(false);
    setHasSearchedOpening(false);
    setHasSearchedSummary(false);
    hasSearchedOpeningRef.current = false;
    hasSearchedSummaryRef.current = false;
    lastSearchValuesRef.current = { opening: {}, summary: {} };
    if (formRef.current) {
      const defaultValues = {
        coName: "",
        coTeID: "",
          rmCode: [] as string[],
        buyersCountry: [],
        productCategory: [],
        productDetail: [],
        openingBalanceStatus: "",
        balanceType: "negative",
        lastUpdatedStartDate: "",
        lastUpdatedEndDate: "",
      };
      formRef.current.resetForm({ values: defaultValues });
    }
    setTableData([]);
    setResetEnable(false);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setRowCount(0);

    if (!isCOUser) {
      setVisible({
        coName: true,
        coTeId: true,
        coNameLabel: true,
        coTeIdLabel: true,
      });
      setDisable({
        coName: false,
        coTeId: false,
        rmCode: true,
      });
    }
  };

  const openingBalanceColumns = useMemo(
    () =>
      [
        {
          accessorKey: "coTeId",
          header: "Certified Organization TE-ID",
          minSize: 120,
          maxSize: 200,
        },
        {
          accessorKey: "coName",
          header: "Certified Organization Name",
          minSize: 120,
          maxSize: 200,
        },
        {
          accessorKey: "Country",
          header: "Country",
          minSize: 80,
          maxSize: 120,
        },
        
        {
          accessorKey: "rawMaterial",
          header: "Raw Material",
          minSize: 200,
          maxSize: 300,
        },
        {
          accessorKey: "productCategory",
          header: "Product Category",
          minSize: 180,
          maxSize: 280,
        },
        {
          accessorKey: "productDetail",
          header: "Product Detail",
          minSize: 200,
          maxSize: 300,
        },
        {
          accessorKey: "transactionDate",
          header: "Opening Balance Transaction Date",
          minSize: 160,
          maxSize: 260,
          Cell: ({ cell, row }: { cell: any; row: any }) => {
            const transactionDate = cell.getValue();
            const weight = row.original.certifiedWeight;
            const isPending = weight === "pending" || transactionDate === "pending";
            return (
              <span style={{ backgroundColor: isPending ? "#ffe0d6" : "transparent", padding: "4px 8px", display: "block" }}>
                {transactionDate}
              </span>
            );
          },
        },
        {
          accessorKey: "certifiedWeight",
          header: "Certified Weight (in Kgs)",
          minSize: 150,
          maxSize: 200,
          Cell: ({ cell, row }: { cell: any; row: any }) => {
            const weight = cell.getValue();
            const transactionDate = row.original.transactionDate;
            const isPending = weight === "pending" || transactionDate === "pending";
            const displayValue = typeof weight === "number" ? weight.toFixed(2) : weight;
            return (
              <span style={{ backgroundColor: isPending ? "#ffe0d6" : "transparent", padding: "4px 8px", display: "block" }}>
                {displayValue}
              </span>
            );
          },
        },
      ] as MRT_ColumnDef<IOpeningBalanceRecord>[],
    []
  );

  const balanceSummaryColumns = useMemo(
    () =>
      [
        {
          accessorKey: "coTeId",
          header: "Certified Organization TE-ID",
          minSize: 120,
          maxSize: 200,
        },
        {
          accessorKey: "coName",
          header: "Certified Organization Name",
          minSize: 120,
          maxSize: 200,
        },
         {
          accessorKey: "scNo",
          header: "Scope Certificate Number",
          minSize: 120,
          maxSize: 200,
        },
         {
          accessorKey: "country",
          header: "Country",
          minSize: 80,
          maxSize: 120,
        },
        {
          accessorKey: "rawMaterial",
          header: "Raw Material",
          minSize: 200,
          maxSize: 300,
        },
        {
          accessorKey: "productCategory",
          header: "Product Category",
          minSize: 180,
          maxSize: 280,
        },
        {
          accessorKey: "productDetail",
          header: "Product Detail",
          minSize: 200,
          maxSize: 300,
        },
        {
          accessorKey: "certifiedWeight",
          header: "Certified Weight Available (in Kgs)",
          minSize: 160,
          maxSize: 260,
          Cell: ({ cell, row }: { cell: any; row: any }) => {
            const raw = cell.getValue();
            const numWeight = typeof raw === "number" && !Number.isNaN(raw) ? raw : Number(raw);
            const isNum = typeof numWeight === "number" && !Number.isNaN(numWeight);
            const isNegative = (row.original as any).isNegative === true || (isNum && numWeight < 0);
            const displayValue = isNum ? numWeight.toFixed(2) : "—";
            return (
              <span style={{ backgroundColor: isNegative ? "#ffe0d6" : "transparent", padding: "4px 8px", display: "block" }}>
                {displayValue}
              </span>
            );
          },
        },
        {
          accessorKey: "lastUpdatedDate",
          header: "Last Updated Date",
          minSize: 150,
          maxSize: 250,
          Cell: ({ row }: { row: any }) => {
            const raw = row.original.certifiedWeight;
            const numWeight = typeof raw === "number" && !Number.isNaN(raw) ? raw : Number(raw);
            const isNum = typeof numWeight === "number" && !Number.isNaN(numWeight);
            const weight = isNum ? numWeight : 0;
            const isNegative = (row.original as any).isNegative === true || (weight != null && weight < 0);
            return (
              <span style={{ backgroundColor: isNegative ? "#ffe0d6" : "transparent", padding: "4px 8px", display: "block" }}>
                {row.original.lastUpdatedDate || "—"}
              </span>
            );
          },
        },
      ] as MRT_ColumnDef<IBalanceSummaryRecord>[],
    []
  );

  const formConfig = getFormConfig();

  return (
    <PageWrapper>
      <Typography variant="h6" gutterBottom align="left" fontWeight={"600"}>
        Material Ledger Report
      </Typography>

      <ButtonContainer>
        <StyledButton
          onClick={() => {
            setReportType("opening-balance");
            setTableData([]);
            setResetEnable(false);
            loadOpeningBalanceData();
          }}
          variant={reportType === "opening-balance" ? "contained" : "outlined"}
          color="warning"
          sx={{ 
            height: "40px", 
            mb: 2,
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            padding: { xs: "8px 12px", sm: "8px 16px", md: "8px 20px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(reportType === "opening-balance" ? {} : {
              backgroundColor: "transparent",
              borderColor: "#F5A623",
              color: "#333",
              "&:hover": {
                backgroundColor: "rgba(245, 166, 35, 0.04)",
              }
            })
          }} 
        >
          Opening Balance Coverage Report
        </StyledButton>
        <StyledButton
          onClick={() => {
            setReportType("balance-summary");
            setTableData([]);
            setResetEnable(false);
            loadBalanceSummaryData();
          }}
          variant={reportType === "balance-summary" ? "contained" : "outlined"}
          color="warning"
          sx={{ 
            height: "40px", 
            mb: 2,
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            padding: { xs: "8px 12px", sm: "8px 16px", md: "8px 20px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(reportType === "balance-summary" ? {} : {
              backgroundColor: "transparent",
              borderColor: "#F5A623",
              color: "#333",
              "&:hover": {
                backgroundColor: "rgba(245, 166, 35, 0.04)",
              }
            })
          }} 
        >
          Material Ledger Balance Summary Report
        </StyledButton>
      </ButtonContainer>

      <FormBuilder
        key={reportType}
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        The data returned in the table/export is limited to the first 10,000 rows. Please apply filters to refine the data if more records exist.
      </Alert>

      <Grid
        item
        xs={12}
        sm={12}
        md={2}
        lg={2}
        display="flex"
        justifyContent={"flex-end"}
        alignItems="center"
        gap={1}
        flexWrap="wrap"
      >
        <StyledButton
          onClick={handleExportData}
          variant="outlined"
          disabled={
          isExporting ||
          isLoading ||
          (tableData.length === 0 && rowCount === 0) ||
          hasFormChangedFromLastSearch
        }
        >
          {isExporting ? "Exporting..." : "Export Data"}
        </StyledButton>
        <StyledButton 
          onClick={handleSubmit}
        >
          Search
        </StyledButton>
        <StyledButton
          onClick={handleReset}
          variant="outlined"
          disabled={!isResetEnabled}
        >
          Reset
        </StyledButton>
      </Grid>

      <CustomTable
        columns={reportType === "opening-balance" ? openingBalanceColumns : balanceSummaryColumns}
        data={tableData}
        title="Material Ledger Report"
        enableTopToolbar={true}
        enableBottomToolbar={true}
        paginations={pagination}
        setPaginations={setPagination}
        rowCount={rowCount}
        isLoadingTodos={isLoading}
        enableRowActions={reportType === "balance-summary"}
        renderRowActions={
          reportType === "balance-summary"
            ? ({ row }) => (
                <ActionsMenu
                  row={row}
                  onView={() => handleViewBalanceSummary(row)}
                />
              )
            : undefined
        }
      />
    </PageWrapper>
  );
};

export default MaterialLedgerReport;
